import clsx from 'clsx'
import type { ReactNode } from 'react'
import { pickableClass } from '@/components/ui/primitives.tsx'

/**
 * The builder's structural furniture.
 *
 * The layout is an outcome requirement in PRD-18: a stable order, nothing behind an
 * "advanced" disclosure, and the preview always reachable. So every section is a numbered
 * glass panel in one column — no tabs, no accordions, nothing collapsed by default. On a
 * narrow viewport the same panels get a stepper, and the stepper is anchor links plus a media
 * query rather than component state: a step you can link to, restore on reload and reach with
 * a keyboard is worth more than one that animates.
 *
 * Two things this module refuses to do, both because the browser suite reads the page through
 * its accessibility tree:
 *
 *   - It never puts the step number inside the `<h2>`. That heading is the section's
 *     accessible name via `aria-labelledby`, and `getByRole('region', { name: 'Knowledge' })`
 *     is how six assertions find their way in.
 *   - It never gives an inner `<section>` an accessible name. A `<section>` only exposes
 *     `role=region` once it has one, so a titled sub-block inside Knowledge would become a
 *     second region matching the same name and the locator would go ambiguous.
 */

export const SECTION_IDS = {
  identity: 'builder-identity',
  instructions: 'builder-instructions',
  knowledge: 'builder-knowledge',
  capabilities: 'builder-capabilities',
  behavior: 'builder-behavior',
  starters: 'builder-starters',
  audience: 'builder-audience',
  preview: 'builder-preview'
} as const

export const BuilderSection = ({
  id,
  step,
  title,
  lead,
  aside,
  children
}: {
  id: string
  /**
   * The position in the maker's path, drawn as a chip beside the title.
   *
   * Optional because the read-only sheet reuses these panels and has no path: the numbers
   * mean "steps to complete", and a page nobody can complete anything on should not count
   * them. Omitting the chip there also spares the reader a sequence with holes in it, since
   * the disclosure panel stands in for three of the builder's sections at once.
   */
  step?: number
  title: string
  lead: string
  aside?: ReactNode
  children: ReactNode
}) => (
  <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-4">
    {/*
      No entrance animation on the panel itself, unlike the card grids elsewhere. A running
      `transform` makes an element a containing block for `position: fixed` descendants, and
      the Knowledge step renders the share dialog from inside here — during the animation the
      modal would be laid out against this panel instead of against the window. The page's
      own `fade-in` is the entrance; a form does not need a staggered one.
    */}
    <div className="card rounded-panel px-4 py-[15px] sm:px-[17px]">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            {/* Hidden from assistive technology: the order is already carried by the document,
                and "3" announced before "Knowledge" is noise in front of every heading. */}
            {step !== undefined && (
              <span
                aria-hidden
                className="readout flex h-[21px] w-[21px] flex-none items-center justify-center rounded-[7px] border border-rule bg-card-solid text-[9.5px] text-fg-subtle"
              >
                {step}
              </span>
            )}
            <h2 id={`${id}-title`} className="font-sans text-[15px] font-semibold tracking-[-0.018em]">
              {title}
            </h2>
          </div>
          <p className="mt-1.5 max-w-2xl text-pretty text-[13px] leading-[1.5] text-fg-muted">{lead}</p>
        </div>
        {aside}
      </header>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  </section>
)

/**
 * A labelled control.
 *
 * The label is the mono micro-label the rest of the instrument uses, and it stays a real
 * `<label htmlFor>` rather than a placeholder floating in the field: `getByLabel('Name')`,
 * `getByLabel('One-line summary')` and `getByLabel('Instructions')` are how the maker journey
 * fills this form, and a placeholder is not a name.
 */
export const Field = ({
  label,
  htmlFor,
  hint,
  children,
  className
}: {
  label: string
  htmlFor: string
  hint?: string
  children: ReactNode
  className?: string
}) => (
  <div className={className}>
    <label htmlFor={htmlFor} className="eyebrow mb-1.5 block text-fg-muted">
      {label}
    </label>
    {children}
    {hint && <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-subtle">{hint}</p>}
  </div>
)

/**
 * A choice with its consequence written underneath.
 *
 * The description changes with the state rather than describing the control, because the
 * maker plane explains outcomes, not mechanisms: "it will say it does not know rather than
 * guess" is the same setting as `answerMode: 'grounded'` and the only half of it a maker
 * should ever have to reason about.
 *
 * That description is deliberately OUTSIDE the `<label>`. The checkbox's accessible name is
 * the label text alone — `getByRole('checkbox', { name: 'Only answer from its knowledge' })` —
 * and folding a sentence of consequence into it would rename the control every time the
 * setting changed.
 */
export const Toggle = ({
  id,
  label,
  on,
  onDescription,
  offDescription,
  disabled,
  onChange
}: {
  id: string
  label: string
  on: boolean
  onDescription: string
  offDescription: string
  disabled?: boolean
  onChange: (next: boolean) => void
}) => (
  <div className={pickableClass(on, disabled)}>
    <input
      id={id}
      type="checkbox"
      checked={on}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className="mt-[3px] h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
    />
    <div className="min-w-0">
      <label htmlFor={id} className="cursor-pointer text-[13.5px] font-semibold tracking-[-0.014em]">
        {label}
      </label>
      <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
        {on ? onDescription : offDescription}
      </p>
    </div>
  </div>
)

/**
 * The narrow-viewport stepper.
 *
 * Rendered always and hidden by the layout above `xl`, where the sections and the preview are
 * visible side by side and an index would be noise. It sits on an opaque strip rather than on
 * the stage's frosted glass: a sticky bar that lets the panels scroll through it turns the
 * whole page into a smear on the one viewport where the page is already tight.
 */
export const StepNav = ({ steps }: { steps: Array<{ id: string; label: string }> }) => (
  <nav
    aria-label="Builder sections"
    className="sticky top-0 z-20 -mx-1 mb-3.5 overflow-x-auto border-b border-rule bg-shell-solid/92 px-1 py-2 backdrop-blur-[14px] xl:hidden"
  >
    <ol className="flex items-center gap-1.5">
      {steps.map((step, index) => (
        <li key={step.id} className="shrink-0">
          <a
            href={`#${step.id}`}
            className="flex h-[28px] items-center gap-[7px] rounded-full border border-rule bg-card px-[11px] text-[12px] tracking-[-0.01em] text-fg-muted transition-[color,border-color,transform] duration-200 hover:-translate-y-px hover:border-rule-strong hover:text-fg"
          >
            <span aria-hidden className="readout text-[9px] text-fg-subtle">
              {index + 1}
            </span>
            {step.label}
          </a>
        </li>
      ))}
    </ol>
  </nav>
)

/** An inline note that pairs an icon with its text — warnings are never colour alone (AGM-124). */
export const Note = ({
  tone,
  icon,
  children
}: {
  tone: 'info' | 'warn' | 'good'
  icon: ReactNode
  children: ReactNode
}) => (
  <p
    className={clsx(
      'flex items-start gap-2 rounded-tile border px-3 py-2 text-pretty text-[12.5px] leading-[1.5]',
      tone === 'info' && 'border-rule bg-card-solid text-fg-muted',
      tone === 'warn' && 'border-alert/35 bg-alert-soft text-fg',
      tone === 'good' && 'border-grounded/35 bg-grounded-soft text-fg'
    )}
  >
    <span className="mt-[2px] shrink-0" aria-hidden>
      {icon}
    </span>
    <span className="min-w-0">{children}</span>
  </p>
)
